import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Button,
  TextField,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Grid,
  Typography,
  CardMedia,
  Card,
  Box,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Snackbar,
  Stack
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Crop,
  AddCircle as AddCircleIcon,
  ContentCut as ContentCutIcon
} from "@mui/icons-material";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import { BIBlue, BIGrey, BIYellow } from "../../assets/helpers";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { Carousel } from "react-responsive-carousel";
import PauseCircleFilledIcon from "@mui/icons-material/PauseCircleFilled";
import PlayCircleFilledIcon from "@mui/icons-material/PlayCircleFilled";
import { Getrequestcall } from "../../request/Getrequest";
import {
  DELETE_MONTAGES_FILE,
  GET_PRODUCT_IMAGES,
  SAVE_MONTAGE,
  SAVE_MONTAGE_FILE,
  SAVE_SITE_MONTAGE_FILES
} from "../../constant/Apipath";
import { DeleteRequestCall } from "../../request/Deletrequest";
import moment from "moment";
import { Postrequestcall } from "../../request/Postrequest";
import { Fileuploadrequestcall } from "../../request/Fileuploadrequest";
import Pageheader from "../common/Pageheader";
import { useSelector, useDispatch } from "react-redux";
import { English } from "../../constant/EnglishFallback";
import PromptModal from "../common/PromptModal";
import Removeduplicates from "../common/Removeduplicate";
import { setSelectedMontage } from "../../redux/actions/actions";

// Import your modals
import GetMusicModal from "./models/GetMusicModal";
import AddPicturesModal from "./models/AddPicturesModal";
import ImageChooseFromModal from "./models/ImageChooseFromModal";
import FreeFormCropModal from "./models/FreeFromCropModal";
import RatioCropModal from "./models/RatioCropModal";
import VideoTrimmerModal from "./VideoTrimmerModal";

const iconButtonOverlayStyle = {
  position: "absolute",
  zIndex: 99,
  backgroundColor: "rgba(0, 0, 0, 0.4)",
  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.6)"
  },
  color: "darkgrey"
};

// Video Item Component to handle imperative playback
// Video Item Component to handle imperative playback
const MontageVideoItem = ({ item, isActive, play, onComplete, onDurationLoad }) => {
  const videoRef = useRef(null);
  const playPromiseRef = useRef(null);
  // Track if we are manually playing (interacting with controls)
  const [isManualPlaying, setIsManualPlaying] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (isActive && play) {
      // Global Montage Play Mode
      const start = parseFloat(item.file?.trimStart || 0);
      // Seek if we are significantly off
      if (
        video.currentTime < start ||
        (Math.abs(video.currentTime - start) > 0.5 && video.currentTime < start + 0.5)
      ) {
        video.currentTime = start;
      }

      const p = video.play();
      if (p !== undefined) {
        playPromiseRef.current = p;
        p.catch((error) => {
          console.warn("Video play prevented:", error);
        });
      }
    } else if (!isManualPlaying) {
      // Only pause if we are not manually playing
      video.pause();
    }
  }, [isActive, play, item, isManualPlaying]);

  const handleTimeUpdate = (e) => {
    const video = e.target;
    // Determine bounds
    const start = parseFloat(item.file?.trimStart || 0);
    const duration = parseFloat(
      item.videoDuration || item.imageDisplayTime || (video.duration ? video.duration : 100)
    );
    const end = start + duration;

    // Enforce Start (if user scrubs before start)
    if (video.currentTime < start) {
      video.currentTime = start;
    }

    // Enforce End
    // Check if we reached the end of the segment
    if (video.currentTime >= end - 0.1) {
      // 0.1s tolerance
      video.pause();
      // If Global Play, trigger next slide
      if (isActive && play) {
        if (onComplete) onComplete();
      } else {
        // Manual Play: just stop and reset to start
        video.currentTime = start;
        setIsManualPlaying(false);
      }
    }
  };

  const handleEnded = () => {
    if (isActive && play && onComplete) {
      onComplete();
    } else {
      setIsManualPlaying(false);
    }
  };

  const handleLoadedMetadata = (e) => {
    if (item.file?.trimStart) {
      e.target.currentTime = item.file.trimStart;
    }
    // Update duration if missing in parent state
    if (onDurationLoad && (!item.videoDuration || item.videoDuration === 0)) {
      onDurationLoad(e.target.duration);
    }
  };

  return (
    <Box
      component="video"
      ref={videoRef}
      src={item?.fileUrl}
      controls={false} // Permanently hide controls as requested
      muted={play ? false : false} // Allow sound in manual mode too
      playsInline
      onLoadedMetadata={handleLoadedMetadata}
      onTimeUpdate={handleTimeUpdate}
      onEnded={handleEnded}
      onPlay={() => !play && setIsManualPlaying(true)}
      onPause={() => setIsManualPlaying(false)}
      sx={{
        width: "100%",
        height: "100%",
        objectFit: "contain"
      }}
    />
  );
};

// Draggable Card Component for react-dnd
const DraggableCard = ({
  item,
  index,
  moveCard,
  stopCount,
  setSelectedImage,
  setCount,
  handleDeletePic,
  handleImageDescriptionChange,
  iconButtonOverlayStyle,
  BIYellow,
  TEXT_ADD_ON_TEXT,
  onCropVideo
}) => {
  const ref = useRef(null);
  const isVideo = item?.imageFrom === "Video";

  const [{ handlerId }, drop] = useDrop({
    accept: "montage-card",
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId()
      };
    },
    hover(draggedItem, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = draggedItem.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        return;
      }

      const hoverBoundingRect = ref.current?.getBoundingClientRect();
      const hoverMiddleX = (hoverBoundingRect.right - hoverBoundingRect.left) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientX = clientOffset.x - hoverBoundingRect.left;

      if (dragIndex < hoverIndex && hoverClientX < hoverMiddleX) {
        return;
      }
      if (dragIndex > hoverIndex && hoverClientX > hoverMiddleX) {
        return;
      }

      moveCard(dragIndex, hoverIndex);
      draggedItem.index = hoverIndex;
    }
  });

  const [{ isDragging }, drag] = useDrag({
    type: "montage-card",
    item: () => {
      return { id: item.id, index };
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    })
  });

  const opacity = isDragging ? 0.4 : 1;
  drag(drop(ref));

  return (
    <span>
      <div
        ref={ref}
        style={{ opacity }}
        data-handler-id={handlerId}
        onClick={() => {
          stopCount();
          setSelectedImage(index);
          setCount(index * 5);
        }}>
        <Card
          className="main-div"
          sx={{
            width: { xs: 100, md: 175 },
            height: { xs: 85, md: 95 }
          }}
          style={{
            position: "relative",
            border: `2px solid ${
              isVideo
                ? "purple"
                : item?.imageFrom === "Blank" || item?.imageFrom === "Gallery"
                  ? BIYellow
                  : item?.imageFrom === "Site"
                    ? "red"
                    : ""
            }`
          }}>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              handleDeletePic(item, index);
            }}
            size="small"
            sx={{
              ...iconButtonOverlayStyle,
              top: "4%",
              left: "2%"
            }}>
            <CloseIcon fontSize="small" />
          </IconButton>

          {/* Trim button for videos */}
          {isVideo && onCropVideo && (
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setSelectedImage(index);
                onCropVideo();
              }}
              size="small"
              sx={{
                ...iconButtonOverlayStyle,
                top: "5%",
                right: "2%"
              }}
              title="Trim Video">
              <ContentCutIcon fontSize="small" />
            </IconButton>
          )}

          {isVideo ? (
            <video
              src={item.fileUrl}
              style={{
                objectFit: "cover",
                height: "100%",
                width: "100%",
                pointerEvents: "none"
              }}
            />
          ) : (
            <CardMedia
              component="img"
              image={item.fileUrl ? item.fileUrl : URL.createObjectURL(item)}
              alt="media"
              style={{ objectFit: "cover", height: "100%" }}
            />
          )}

          <Box className="card-desc-div">
            <TextField
              className="card-descriptions"
              placeholder={TEXT_ADD_ON_TEXT}
              type="text"
              variant="standard"
              value={item.imageDescription ?? ""}
              onChange={(e) => handleImageDescriptionChange(e, item.id)}
              onClick={(e) => e.stopPropagation()}
              sx={{
                width: "100%",
                input: {
                  color: "white",
                  textAlign: "center",
                  pl: 1,
                  pr: 1,
                  "&::placeholder": {
                    color: "white",
                    opacity: 0.8,
                    textAlign: "center"
                  }
                }
              }}
            />
          </Box>

          {isVideo && (
            <Box
              sx={{
                position: "absolute",
                bottom: 2,
                right: 2,
                backgroundColor: "rgba(0,0,0,0.7)",
                px: 0.5,
                py: 0.25,
                borderRadius: 0.5
              }}>
              <Typography variant="caption"  sx={{color: "white",  fontSize: "0.65rem" }}>
                {item.videoDuration?.toFixed(1)}s
              </Typography>
            </Box>
          )}
        </Card>
      </div>
    </span>
  );
};

export default function MontageForm() {
  const { action, id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  // Redux selectors
  const loginData = useSelector((state) => state.main?.loginData);
  const userID = useSelector((state) => state.main?.loginData?.userInformation?.UserId);
  const selectedMontage = useSelector((state) => state.main?.selectedMontage);
  const { multiLanguageData } = useSelector((state) => state?.main);

  // Local state
  const [mode, setMode] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pictureList, setPictureList] = useState([]);
  const [previewFlag, setPreviewFlag] = useState(true);
  const [getMusicModalOpen, setGetMusicModalOpen] = useState(false);
  const [musicFileUrl, setMusicFileUrl] = useState("");
  const [play, setPlay] = useState(false);
  const [musicName, setMusicName] = useState("Add Sound");
  const [addPictureModalOpen, setAddPictureModalOpen] = useState(false);
  const [imageChooseFromModalOpen, setImageChooseFromModalOpen] = useState(false);
  const [pictureFile, setPictureFile] = useState([]);
  const [addIconPosition, setAddIconPosition] = useState(0);
  const [selectedFromEmpty, setSelectedFromEmpty] = useState(false);
  const [imageCount, setImageCount] = useState(0);
  const [selectedImage, setSelectedImage] = useState(0);
  const [showFreeFormModal, setShowFreeFormModal] = useState(false);
  const [showRatioModal, setShowRatioModal] = useState(false);
  const [loadingImages, setLoadingImages] = useState(true);
  const [snackFlag, setSnackFlag] = useState(false);

  // NEW: Video-related state
  const [videoTrimmerOpen, setVideoTrimmerOpen] = useState(false);
  const [selectedVideoFile, setSelectedVideoFile] = useState(null);
  const [totalDuration, setTotalDuration] = useState(0);
  const [videoCount, setVideoCount] = useState(0);
  const [isRetrimming, setIsRetrimming] = useState(false);

  // Form specific state
  const [montageName, setMontageName] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [count, setCount] = useState(0);
  const [toggleBtn, setToggleBtn] = useState(false);
  const [deletedImages, setDeletedImages] = useState([]);
  const [isDiscardModalOpen, setIsDiscardModalOpen] = useState(false);
  const [isSaveLoading, setIsSaveLoading] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Utilities
  const removeDuplicates = Removeduplicates;

  const LanguageData = multiLanguageData?.Montages || {};
  const {
    TEXT_ADD_MONTAGE = English.Montages.TEXT_ADD_MONTAGE,
    TEXT_EDIT_MONTAGE = English.Montages.TEXT_EDIT_MONTAGE,
    TEXT_BACK_BUTTON = English.Montages.TEXT_BACK_BUTTON,
    BUTTON_ADD_SOUND = English.Montages.BUTTON_ADD_SOUND,
    TEXT_TITLE = English.Montages.TEXT_TITLE,
    BUTTON_FREEFROM = English.Montages.BUTTON_FREEFROM,
    BUTTON_RATIO = English.Montages.BUTTON_RATIO,
    TEXT_DIALOG_MONTAGE_NAME = English.Montages.TEXT_DIALOG_MONTAGE_NAME,
    TEXT_DIALOG_ADD_PICTURES = English.Montages.TEXT_DIALOG_ADD_PICTURES,
    TEXT_ALERT_IMAGE_SELECT = English.Montages.TEXT_ALERT_IMAGE_SELECT,
    TEXT_ADD_ON_TEXT = English.Montages.TEXT_ADD_ON_TEXT,
    TEXT_SNACKBAR_PAGE_ADDED = English.Montages.TEXT_SNACKBAR_PAGE_ADDED,
    TEXT_DIALOG_TITLE = English.Montages.TEXT_DIALOG_TITLE,
    TEXT_DIALOG_MESSAGE = English.Montages.TEXT_DIALOG_MESSAGE,
    BUTTON_DIALOG_DISCARD = English.Montages.BUTTON_DIALOG_DISCARD,
    BUTTON_TEXT_CANCEL = English.Montages.BUTTON_TEXT_CANCEL
  } = LanguageData || {};

  const [initialFormData, setInitialFormData] = useState({
    montageName: "",
    pictureList: [],
    musicFileUrl: "",
    musicName: BUTTON_ADD_SOUND
  });

  // Update musicName when language changes
  useEffect(() => {
    if (musicName === "Add Sound" || musicName === BUTTON_ADD_SOUND) {
      setMusicName(BUTTON_ADD_SOUND);
    }
  }, [BUTTON_ADD_SOUND, musicName]);

  // NEW: Calculate total duration and counts
  useEffect(() => {
    let duration = 0;
    let imgCount = 0;
    let vidCount = 0;

    pictureList.forEach((item) => {
      const isVideo =
        item?.imageFrom === "Video" ||
        item.fileType?.toLowerCase() === "video" ||
        item.fileUrl?.endsWith(".mp4");

      if (isVideo) {
        // Check various possible duration fields
        const vidDuration = parseFloat(
          item.videoDuration ||
            item.duration ||
            item.Duration ||
            item.trimDuration ||
            item.imageDisplayTime ||
            0
        );
        duration += vidDuration;
        vidCount++;
      } else {
        duration += 5; // Each image is 5 seconds
        if (item?.imageFrom === "Gallery") {
          imgCount++;
        }
      }
    });

    setTotalDuration(duration);
    setImageCount(imgCount);
    setVideoCount(vidCount);
  }, [pictureList]);

  // Move card function for react-dnd
  const moveCard = useCallback((dragIndex, hoverIndex) => {
    setPictureList((prevCards) => {
      const newCards = [...prevCards];
      const draggedCard = newCards[dragIndex];
      newCards.splice(dragIndex, 1);
      newCards.splice(hoverIndex, 0, draggedCard);
      return newCards;
    });
  }, []);

  // Update video duration callback
  const updateItemDuration = useCallback((id, duration) => {
    setPictureList((prev) =>
      prev.map((item) => {
        if (
          item.id === id &&
          (!item.videoDuration || Math.abs(item.videoDuration - duration) > 0.5)
        ) {
          return { ...item, videoDuration: duration };
        }
        return item;
      })
    );
  }, []);

  const stopCount = () => {
    setPlay(false);
    setCount(0);

    const sound = document.getElementById("fromSitePicture");
    if (sound) {
      try {
        sound.pause();
        sound.currentTime = 0;
      } catch (error) {
        console.warn("Error stopping audio:", error);
      }
    }
  };

  const handleDeletePic = async (item, index) => {
    if (item?.imageFrom === "Gallery") {
      setImageCount(imageCount - 1);
    }
    if (item?.imageFrom === "Video") {
      setVideoCount(videoCount - 1);
    }

    let pictures = [];
    await deletedImages.push(item);
    deletedImages.map((deletePic) => {
      pictures = pictureList.filter((pic) => pic?.id !== deletePic?.id);
      return deletePic;
    });
    setDeletedImages(deletedImages);
    setPictureList(pictures);

    if (pictures?.length === 0) {
      setCount(0);
    } else {
      if (index === 0) {
        setCount(0);
      } else {
        setCount(pictures?.length * 4);
      }
    }
  };

  const handleImageDescriptionChange = (e, id) => {
    let inputValue = e.target.value;
    inputValue = inputValue.replace(/^[^A-Za-z0-9]+/, "");
    let a = [];
    const tempArr = pictureList.map((item) => {
      a = item;
      if (id == item.id) {
        item.imageDescription = inputValue;
      }
      return a;
    });
    setPictureList(tempArr);
  };

  // NEW: Handle video trim completion
  const handleVideoTrimComplete = (trimmedFile, duration) => {
    const videoUrl = URL.createObjectURL(trimmedFile);

    const videoItem = {
      id: Date.now(),
      fileUrl: videoUrl,
      file: trimmedFile,
      galleryUploadUrl: trimmedFile,
      imageFrom: "Video",
      videoDuration: duration,
      imageDescription: "",
      type: "video/mp4",
      name: `video_${Date.now()}`
    };

    if (isRetrimming) {
      // Replace the existing video at selectedImage index
      console.log(
        "Re-trimming video at index:",
        selectedImage,
        "Total videos:",
        pictureList.length
      );

      setPictureList((prevList) => {
        const newList = [...prevList];
        const existingVideo = newList[selectedImage];

        console.log("Existing video:", existingVideo);

        if (existingVideo) {
          newList[selectedImage] = {
            ...videoItem,
            id: existingVideo.id, // Keep the same ID
            imageDescription: existingVideo.imageDescription || "" // Keep description
          };
        } else {
          // If for some reason the video doesn't exist, just add it
          console.warn("Video not found at index, adding instead");
          newList.push(videoItem);
        }

        return newList;
      });
      setIsRetrimming(false);
    } else {
      // Adding a new video
      if (selectedFromEmpty) {
        setPictureList([...pictureList, videoItem]);
      } else {
        const newList = [...pictureList];
        newList.splice(addIconPosition + 1, 0, videoItem);
        setPictureList(newList);
      }
    }

    setSnackFlag(true);
    setSelectedVideoFile(null);
  };

  // Handle trimming an existing video in the montage
  const handleTrimExistingVideo = () => {
    const currentVideo = pictureList[selectedImage];
    if (currentVideo && currentVideo.imageFrom === "Video") {
      setIsRetrimming(true); // Set flag to indicate we're re-trimming

      // Convert the video file/URL to a File object if needed
      if (currentVideo.file) {
        setSelectedVideoFile(currentVideo.file);
        setVideoTrimmerOpen(true);
      } else if (currentVideo.galleryUploadUrl) {
        setSelectedVideoFile(currentVideo.galleryUploadUrl);
        setVideoTrimmerOpen(true);
      } else {
        // If it's just a URL, we need to fetch it as a blob
        fetch(currentVideo.fileUrl)
          .then((res) => res.blob())
          .then((blob) => {
            const file = new File([blob], "video.mp4", { type: "video/mp4" });
            setSelectedVideoFile(file);
            setVideoTrimmerOpen(true);
          })
          .catch((err) => {
            console.error("Error loading video for trim:", err);
            alert("Could not load video for trimming");
            setIsRetrimming(false); // Reset flag on error
          });
      }
    }
  };

  const renderCard = useCallback(
    (item, index) => {
      return (
        <React.Fragment key={item.id}>
          <DraggableCard
            index={index}
            item={item}
            moveCard={moveCard}
            stopCount={stopCount}
            setSelectedImage={setSelectedImage}
            setCount={setCount}
            handleDeletePic={handleDeletePic}
            handleImageDescriptionChange={handleImageDescriptionChange}
            iconButtonOverlayStyle={iconButtonOverlayStyle}
            BIYellow={BIYellow}
            TEXT_ADD_ON_TEXT={TEXT_ADD_ON_TEXT}
            onCropVideo={handleTrimExistingVideo}
          />
          {pictureList.length - 1 !== index ? (
            <span
              onClick={() => {
                setAddIconPosition(index);
                setSelectedFromEmpty(false);
                setImageChooseFromModalOpen(true);
              }}
              style={{
                marginLeft: "5px",
                marginRight: "5px",
                cursor: "pointer",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center"
              }}>
              <AddCircleIcon style={{ color: BIBlue }} />
            </span>
          ) : (
            <></>
          )}
        </React.Fragment>
      );
    },
    [moveCard, pictureList.length, BIBlue, TEXT_ADD_ON_TEXT, handleTrimExistingVideo]
  );

  // Initialize mode based on URL params
  useEffect(() => {
    if (action === "edit" && id) {
      setMode("edit");
      setLoading(true);
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    } else {
      setMode("add");
      dispatch(setSelectedMontage(null));
    }
  }, [action, id, dispatch]);

  // Load selected montage data
  useEffect(() => {
    if (selectedMontage && mode === "edit") {
      const musicFileName = selectedMontage?.musicUrl
        ?.split("/")
        .pop()
        .replace(/([A-Z])/g, " $1")
        .trim()
        .replace(".mp3", "");

      const processedMusicName = musicFileName
        ? musicFileName.replace(/[0-9$@%]/g, "")
        : BUTTON_ADD_SOUND;

      const initialData = {
        montageName: selectedMontage.name || "",
        pictureList: (selectedMontage?.fileList || []).map((item) => ({
          ...item,
          imageFrom:
            item.fileType?.toLowerCase() === "video" || item.fileUrl?.endsWith(".mp4")
              ? "Video"
              : "Image",
          videoDuration: item.imageDisplayTime // Map stored duration to videoDuration
        })),
        musicFileUrl: selectedMontage.musicUrl || "",
        musicName: processedMusicName
      };

      setMontageName(initialData.montageName);
      setPictureList(initialData.pictureList);
      setMusicName(initialData.musicName);
      setMusicFileUrl(initialData.musicFileUrl);

      setInitialFormData(initialData);
      setHasUnsavedChanges(false);
    } else if (mode === "add") {
      const emptyData = {
        montageName: "",
        pictureList: [],
        musicFileUrl: "",
        musicName: BUTTON_ADD_SOUND
      };
      setInitialFormData(emptyData);
      setHasUnsavedChanges(false);
    }
  }, [selectedMontage, mode, BUTTON_ADD_SOUND]);

  useEffect(() => {
    const currentData = {
      montageName,
      pictureList,
      musicFileUrl,
      musicName
    };

    const hasChanges = !deepEqual(currentData, initialFormData);
    setHasUnsavedChanges(hasChanges);
  }, [montageName, pictureList, musicFileUrl, musicName, initialFormData]);

  // Fetch project pictures
  const fetchUserprojectpicture = async () => {
    try {
      setLoadingImages(true);
      const data = await Getrequestcall(`${GET_PRODUCT_IMAGES}/${userID}`, loginData?.accessToken);
      const temp = data?.data?.data;
      let uniqueArray = removeDuplicates(temp, "projectId");
      const pictures = uniqueArray.map((unique) => {
        let a = unique;
        a["file"] = [];
        temp.map((tempData) => {
          if (tempData?.projectId === a?.projectId) {
            let obj = {
              id: tempData?.id,
              fileUrl: tempData?.imageUrl,
              thumbImageUrl: tempData?.thumbImageUrl,
              dateCreated: tempData?.dateCreated,
              isSelected: false,
              projectId: tempData?.projectId,
              projectName: tempData?.projectName,
              selectCount: 0,
              imageFrom: "Project"
            };
            a["file"].push(obj);
          }
        });
        return a;
      });

      setPictureFile(pictures);
      setLoadingImages(false);
    } catch (error) {
      console.log("fetchUserprojectpicture-> Error While Fetching User Project Picture: ", error);
    } finally {
      setLoadingImages(false);
    }
  };

  useEffect(() => {
    if (addPictureModalOpen) {
      fetchUserprojectpicture();
    }
  }, [addPictureModalOpen]);

  useEffect(() => {
    return () => {
      setMontageName("");
      setPictureList([]);
      setPictureFile([]);
      setMusicFileUrl("");
      setMusicName(BUTTON_ADD_SOUND);
      setPreviewFlag(true);
    };
  }, [BUTTON_ADD_SOUND]);

  // Save montage
  const handleSave = async () => {
    if (montageName.trim() === "") {
      alert(TEXT_DIALOG_MONTAGE_NAME);
      return;
    }
    if (pictureList.length === 0) {
      alert(TEXT_DIALOG_ADD_PICTURES);
      return;
    }

    // NEW: Validate total duration
    if (totalDuration > 60) {
      alert("Total montage duration cannot exceed 60 seconds");
      return;
    }

    // NEW: Validate counts
    const currentImageCount = pictureList.filter((item) => item?.imageFrom !== "Video").length;
    const currentVideoCount = pictureList.filter((item) => item?.imageFrom === "Video").length;

    if (currentImageCount > 5) {
      alert("Maximum 5 images allowed");
      return;
    }

    if (currentVideoCount > 2) {
      alert("Maximum 2 videos allowed");
      return;
    }

    try {
      setIsSaveLoading(true);

      if (mode === "edit") {
        deletedImages.length !== 0 &&
          deletedImages.map(async (deletePic) => {
            if (deletePic?.id) {
              await DeleteRequestCall(
                `${DELETE_MONTAGES_FILE}${deletePic?.id}/${userID}`,
                loginData?.accessToken
              );
            }
          });

        pictureList?.length !== 0 &&
          pictureList.map(async (pic) => {
            if (pic?.deleteCropPicID) {
              await DeleteRequestCall(
                `${DELETE_MONTAGES_FILE}${pic?.deleteCropPicID}/${userID}`,
                loginData?.accessToken
              );
            }
          });
      }

      const obj = {
        Id: selectedMontage?.id || 0,
        Name: montageName || "",
        MusicUrl: musicFileUrl ?? "",
        Description: "",
        ModifiedBy: userID
      };
      const newMontageId = await Postrequestcall(SAVE_MONTAGE, obj, loginData?.accessToken);

      if (newMontageId.data.data === 0) {
        alert(`Montage with ${montageName} name already exist!`);
        return;
      }

      if (pictureList.length !== 0) {
        const promiseRes = await Promise.all(
          pictureList.map(async (item, index) => {
            let res = null;

            // Upload files from Gallery or Video
            if (item?.imageFrom === "Gallery" || item?.imageFrom === "Video") {
              const formData = new FormData();
              formData.append("UserId", userID);

              if (item?.imageFrom === "Video") {
                formData.append("PictureUrl", item.file || item.galleryUploadUrl);
                formData.append("Duration", item.videoDuration);
                // NEW: Append trim metadata
                formData.append("StartTime", item.file?.trimStart || 0);
                formData.append("EndTime", item.file?.trimEnd || item.videoDuration);
                formData.append("TrimDuration", item.videoDuration);

                formData.append("FileName", "test.mp4");
                formData.append("FileType", "video/mp4");
                formData.append("Type", "video/mp4");
                formData.append("Name", "test.mp4");
              } else {
                formData.append("PictureUrl", item.galleryUploadUrl);
                formData.append("FileType", "image/jpeg");
              }

              res = await Fileuploadrequestcall(
                SAVE_SITE_MONTAGE_FILES,
                formData,
                loginData?.accessToken
              );
            }

            const obj = {
              Id: item?.imageFrom && item?.imageFrom !== "" ? 0 : (item?.id ?? 0),
              MontageId: newMontageId?.data?.data,
              SiteId: item.siteId,
              FileUrl: res != null ? res?.data?.data : item?.fileUrl,
              FileOrder: index + 1,
              ModifiedBy: userID,
              ImageDescription: item.imageDescription ?? "",
              DateModified: moment().format("YYYY-MM-DD"),
              FileName: item?.imageFrom === "Video" ? "test.mp4" : "test.jpg",
              FileType: item?.imageFrom === "Video" ? "video" : "image",
              Duration: item?.imageFrom === "Video" ? item.videoDuration : 5
            };

            await Postrequestcall(SAVE_MONTAGE_FILE, obj, loginData?.accessToken);
          })
        );

        if (promiseRes.length !== 0) {
          setPictureList([]);
          setMusicName("");
          setSelectedImage(0);
          setImageCount(0);
          setVideoCount(0);
          navigate("/montages");
        }
      }
      setIsSaveLoading(false);
    } catch (error) {
      console.log("handleSave-> Error While Saving Montage: ", error);
      setIsSaveLoading(false);
    } finally {
      setImageCount(0);
      setVideoCount(0);
      setIsSaveLoading(false);
    }
  };

  // Handle Slide Advancement
  const handleNextSlide = useCallback(() => {
    if (selectedImage < pictureList.length - 1) {
      setSelectedImage((prev) => prev + 1);
    } else {
      // End of Montage
      setPlay(false);
      setSelectedImage(0);
      setCount(0);

      // Stop Audio
      const sound = document.getElementById("fromSitePicture");
      if (sound) {
        sound.pause();
        sound.currentTime = 0;
      }
    }
  }, [selectedImage, pictureList.length]);

  // Image Timer Effect
  useEffect(() => {
    if (!play) return;

    // If current item is an Image, we control timing.
    // If Video, MontageVideoItem controls timing via onComplete.
    const currentItem = pictureList[selectedImage];
    if (currentItem && currentItem.imageFrom !== "Video") {
      const timer = setTimeout(() => {
        handleNextSlide();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [play, selectedImage, pictureList, handleNextSlide]);

  // UI Counter Effect (Approximate)
  useEffect(() => {
    let interval = null;
    if (play) {
      interval = setInterval(() => {
        setCount((prev) => prev + 1);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [play]);

  const handlePlay = () => {
    const sound = document.getElementById("fromSitePicture");

    if (play === true) {
      // Stop
      setPlay(false);
      if (sound) {
        try {
          sound.pause();
          // Don't reset time on pause? Or do we? Logic usually pauses.
        } catch (error) {
          console.warn("Error pausing audio:", error);
        }
      }
    } else {
      // Play
      setPlay(true);
      if (selectedImage === 0) {
        setCount(0);
      }

      if (pictureList.length !== 0 && musicFileUrl !== "" && sound) {
        try {
          // Sync sound if needed?
          // For now just play.
          sound.play().catch((playError) => {
            console.warn("Audio play failed:", playError);
          });
          sound.muted = false;
        } catch (error) {
          console.warn("Error starting audio:", error);
        }
      }
    }
  };

  // Menu handlers
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleFreeFormClick = () => {
    handleClose();
    if (pictureList && pictureList?.length === 0) {
      alert(TEXT_ALERT_IMAGE_SELECT);
      return;
    }

    // Check if selected item is a video
    if (pictureList[selectedImage]?.imageFrom === "Video") {
      alert("Cannot crop videos. Please select an image.");
      return;
    }

    if (pictureList) {
      setShowFreeFormModal(true);
    }
  };

  const handleMusicModelOpen = () => {
    if (isSaveLoading) return;
    setGetMusicModalOpen(true);
  };

  const handleRatioClick = () => {
    handleClose();
    if (pictureList && pictureList?.length === 0) {
      alert(TEXT_ALERT_IMAGE_SELECT);
      return;
    }

    // Check if selected item is a video
    if (pictureList[selectedImage]?.imageFrom === "Video") {
      alert("Cannot crop videos. Please select an image.");
      return;
    }

    if (pictureList) {
      setShowRatioModal(true);
    }
  };

  // Sync Counter with Selected Slide
  useEffect(() => {
    if (!pictureList || pictureList.length === 0) {
      setCount(0);
      return;
    }

    let startTime = 0;
    for (let i = 0; i < selectedImage; i++) {
      const item = pictureList[i];
      const isVideo =
        item?.imageFrom === "Video" ||
        item.fileType?.toLowerCase() === "video" ||
        item.fileUrl?.endsWith(".mp4");
      if (isVideo) {
        const d = parseFloat(
          item.videoDuration ||
            item.duration ||
            item.Duration ||
            item.trimDuration ||
            item.imageDisplayTime ||
            0
        );
        startTime += d;
      } else {
        startTime += 5;
      }
    }
    // Round to integer for display, or keep float if needed. User saw "5 sec", likely integer.
    setCount(Math.round(startTime));
  }, [selectedImage, pictureList]);

  // Navigation handlers
  const goBackAndReset = () => {
    setMontageName("");
    setPictureList([]);
    setPictureFile([]);
    setMusicFileUrl("");
    setMusicName(BUTTON_ADD_SOUND);
    setPreviewFlag(true);
    navigate("/montages");
    handleCloseSnackBar();
    setSelectedImage(0);
    setImageCount(0);
    setVideoCount(0);
  };

  const handleBack = () => {
    if (hasUnsavedChanges) {
      setIsDiscardModalOpen(true);
    } else {
      goBackAndReset();
    }
  };

  const handleCloseSnackBar = () => {
    setSnackFlag(false);
  };

  const deepEqual = (obj1, obj2) => {
    if (obj1 === obj2) return true;
    if (obj1 == null || obj2 == null) return false;
    if (typeof obj1 !== typeof obj2) return false;

    if (Array.isArray(obj1)) {
      if (!Array.isArray(obj2) || obj1.length !== obj2.length) return false;
      return obj1.every((item, index) => deepEqual(item, obj2[index]));
    }

    if (typeof obj1 === "object") {
      const keys1 = Object.keys(obj1);
      const keys2 = Object.keys(obj2);
      if (keys1.length !== keys2.length) return false;
      return keys1.every((key) => deepEqual(obj1[key], obj2[key]));
    }

    return obj1 === obj2;
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh"
        }}>
        <Typography variant="h6">
          <CircularProgress disableShrink />
        </Typography>
      </Box>
    );
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <Box className="main-content-Box" sx={{ p: isSmallScreen ? 2 : 3, pt: 2 }}>
        <Snackbar
          autoHideDuration={3000}
          open={snackFlag}
          onClose={handleCloseSnackBar}
          message={TEXT_SNACKBAR_PAGE_ADDED}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          key={"bottomcenter"}
        />
        <Pageheader headerName={mode === "add" ? TEXT_ADD_MONTAGE : TEXT_EDIT_MONTAGE} />
        <Card className="main-content-card">
          {musicFileUrl && <audio id="fromSitePicture" src={musicFileUrl} />}
          <Grid container sx={{ gap: { xs: 0 }, alignItems: "center" }}>
            <Grid size={{ xs: 8, sm: 8, md: 8, lg: 4, xl: 4 }}>
              <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "row", gap: 1 }}>
                <Button
                  variant="contained"
                  disabled={isSaveLoading}
                  onClick={handleBack}
                  sx={{ width: "50%" }}>
                  {TEXT_BACK_BUTTON}
                </Button>
                <TextField
                  type="text"
                  label={TEXT_TITLE}
                  variant="outlined"
                  size="small"
                  disabled={isSaveLoading}
                  value={montageName}
                  onChange={(e) => {
                    let inputValue = e.target.value;
                    inputValue = inputValue.replace(/^[^A-Za-z0-9]+/, "");
                    setMontageName(inputValue);
                  }}
                  fullWidth
                />
              </Box>
            </Grid>
            <Grid
              sx={{ display: { xs: "none", lg: "flex"  }}}
              size={{ xs: 12, sm: 12, md: 12, lg: 4, xl: 4 }}>
              <ListItemText
                style={{
                  color: isSaveLoading ? BIGrey : "var(--buildid-blue)",
                  cursor: "pointer",
                  textAlign: "center"
                }}
                onClick={handleMusicModelOpen}>
                {musicName ? musicName : BUTTON_ADD_SOUND}
              </ListItemText>
            </Grid>
            <Grid size={{ xs: 4, sm: 4, md: 4, lg: 4, xl: 4 }}>
              <Stack direction="row"  sx={{ gap: 1, justifyContent: "flex-end", display: "flex" }} >
                <IconButton
                  disabled={isSaveLoading}
                  sx={{ p: isSmallScreen ? 0 : 1 }}
                  color="primary"
                  onClick={() => {
                    setSelectedImage(0);
                    stopCount();
                  }}
                  aria-label="Replay"
                  size="medium">
                  <RefreshIcon fontSize={isSmallScreen ? "small" : "medium"} />
                </IconButton>
                <IconButton
                  sx={{ p: isSmallScreen ? 0 : 1 }}
                  onClick={handleClick}
                  disabled={isSaveLoading}
                  aria-label="Crop"
                  color="primary"
                  size="medium">
                  <Crop fontSize={isSmallScreen ? "small" : "medium"} />
                </IconButton>
                <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
                  <MenuItem onClick={handleFreeFormClick}>{BUTTON_FREEFROM}</MenuItem>
                  <MenuItem onClick={handleRatioClick}>{BUTTON_RATIO}</MenuItem>
                </Menu>
                <IconButton
                  sx={{ p: isSmallScreen ? 0 : 1 }}
                  onClick={handleSave}
                  disabled={isSaveLoading}
                  aria-label="Save"
                  color="primary"
                  size="medium">
                  {isSaveLoading ? (
                    <CircularProgress
                      size={isSmallScreen ? 18 : 22}
                      thickness={5}
                      color="inherit"
                    />
                  ) : (
                    <SaveIcon fontSize={isSmallScreen ? "small" : "medium"} />
                  )}
                </IconButton>
              </Stack>
            </Grid>
            <Grid
              sx={{ display: { xs: "flex", lg: "none"  }}}
              size={{ xs: 12, sm: 12, md: 12, lg: 4, xl: 4 }}>
              <ListItemText
                style={{
                  color: BIBlue,
                  cursor: "pointer",
                  textAlign: "center"
                }}
                onClick={handleMusicModelOpen}>
                {musicName ? musicName : BUTTON_ADD_SOUND}
              </ListItemText>
            </Grid>
          </Grid>

          <Box className="main-content-Box">
            <Card
              className="main-content-card"
              sx={{ p: 0, display: "flex", justifyContent: "center", alignItems: "center" }}>
              {previewFlag ? (
                <>
                  {pictureList.length !== 0 ? (
                    <Box
                      onMouseOver={() => setToggleBtn(true)}
                      onMouseOut={() => setToggleBtn(false)}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        width: "100%"
                      }}>
                      <Box sx={{ position: "relative", width: "100%" }}>
                        <IconButton
                          size="large"
                          sx={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            color: BIBlue,
                            zIndex: 999,
                            opacity: toggleBtn ? 1 : 0.6,
                            transition: "opacity 0.3s ease",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center"
                          }}
                          onClick={handlePlay}>
                          {play ? (
                            <PauseCircleFilledIcon fontSize="large" />
                          ) : (
                            <PlayCircleFilledIcon fontSize="large" />
                          )}
                        </IconButton>
                        <Carousel
                          autoPlay={false} // We control navigation manually via startCount
                          infiniteLoop
                          interval={5000} // Unused when autoPlay is false
                          showArrows={false}
                          showThumbs={false}
                          showStatus={false}
                          onChange={(index) => setSelectedImage(index)}
                          showIndicators={false}
                          selectedItem={selectedImage}
                          stopOnHover={false}>
                          {pictureList.map((item, index) => (
                            <Box
                              key={item?.id}
                              sx={{
                                position: "relative",
                                width: "100%",
                                height: { xs: 300, sm: 400, md: 400 }
                              }}>
                              {item?.imageFrom === "Video" ? (
                                <MontageVideoItem
                                  item={item}
                                  isActive={selectedImage === index}
                                  play={play}
                                  onComplete={handleNextSlide}
                                  onDurationLoad={(d) => updateItemDuration(item.id, d)}
                                />
                              ) : (
                                <Box
                                  component="img"
                                  src={item?.fileUrl}
                                  alt=""
                                  loading="lazy"
                                  sx={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "contain"
                                  }}
                                />
                              )}

                              {item?.imageDescription && (
                                <Box
                                  sx={{
                                    position: "absolute",
                                    bottom: 0,
                                    left: 0,
                                    width: "100%",
                                    backgroundColor: "rgba(0, 0, 0, 0.6)",
                                    px: 2,
                                    py: 1,
                                    zIndex: 10
                                  }}>
                                  <TextField
                                     placeholder={TEXT_ADD_ON_TEXT}
                                     type="text"
                                     variant="standard"
                                     value={item.imageDescription ?? ""}
                                     onChange={(e) => handleImageDescriptionChange(e, item.id)}
                                     fullWidth
                                     multiline
                                     maxRows={5}
                                     InputProps={{
                                       disableUnderline: true,
                                       sx: {
                                         color: "white !important",
                                         textAlign: "center !important",
                                         "& input": {
                                           color: "white !important",
                                           textAlign: "center !important"
                                         },
                                         "&::placeholder": {
                                           color: "white !important",
                                           opacity: "0.8 !important",
                                           textAlign: "center !important"
                                         }
                                       }
                                     }}
                                     inputProps={{
                                       style: {
                                         color: "white",
                                         textAlign: "center"
                                       }
                                     }}
                                     sx={{
                                       width: "100%",
                                       "& .MuiInputBase-input": {
                                         color: "white !important",
                                         textAlign: "center !important"
                                       },
                                       "& .MuiInputBase-inputMultiline": {
                                         color: "white !important",
                                         textAlign: "center !important"
                                       }
                                     }}
                                   />
                                </Box>
                              )}
                            </Box>
                          ))}
                        </Carousel>
                      </Box>
                    </Box>
                  ) : (
                    <IconButton size="large">
                      <PlayCircleFilledIcon fontSize="large" />
                    </IconButton>
                  )}
                </>
              ) : (
                <></>
              )}
            </Card>

            <Stack sx={{ display: "flex", alignItems: "center" }} m={0.5}>
              <Typography>
                {count} sec / {totalDuration.toFixed(1)} sec total
              </Typography>
            </Stack>

            <Box
              sx={{ justifyContent: pictureList?.length > 0 ? "space-between" : "center",  display: "flex" }}
              >
              <Box
                sx={{
                  display: "flex",
                  width: "auto",
                  overflow: "scroll",
                  mr: 1.5,
                  scrollbarWidth: "none"
                }}>
                {pictureList?.length > 0 && (
                  <div
                    style={{
                      display: "-webkit-inline-box",
                      width: "100%"
                    }}>
                    {pictureList?.length > 0 ? (
                      pictureList.map((item, index) => renderCard(item, index))
                    ) : (
                      <img style={{ display: "none" }} alt="" />
                    )}
                  </div>
                )}
              </Box>
              <Box>
                <Button
                  fullWidth
                  variant="outlined"
                  sx={{
                    width: { xs: 100, md: 175 },
                    height: { xs: 85, md: 95 },
                    borderStyle: "solid",
                    color: BIBlue
                  }}
                  onClick={() => {
                    if (play) {
                      alert("Please stop the montage play before selecting images.");
                      return;
                    }
                    setImageChooseFromModalOpen(true);
                    setSelectedFromEmpty(true);
                  }}>
                  <AddIcon fontSize="small" />
                </Button>
              </Box>
            </Box>
          </Box>
        </Card>

        {/* All Modals */}
        <GetMusicModal
          open={getMusicModalOpen}
          onClose={() => setGetMusicModalOpen(false)}
          loginData={loginData}
          setMusicName={setMusicName}
          setMusicFileUrl={setMusicFileUrl}
          pictureList={pictureList}
          play={play}
          setPlay={setPlay}
        />

        <AddPicturesModal
          open={addPictureModalOpen}
          onClose={() => setAddPictureModalOpen(false)}
          pictureFile={pictureFile}
          setPictureList={setPictureList}
          pictureList={pictureList}
          selectedFromEmpty={selectedFromEmpty}
          addIconPosition={addIconPosition}
          loginData={loginData}
          userID={userID}
          loadingImages={loadingImages}
          setLoadingImages={setLoadingImages}
          setImageChooseFromModalOpen={setImageChooseFromModalOpen}
          setSelectedFromEmpty={setSelectedFromEmpty}
          totalDuration={totalDuration}
        />

        <ImageChooseFromModal
          open={imageChooseFromModalOpen}
          onClose={() => setImageChooseFromModalOpen(false)}
          setAddPictureModalOpen={setAddPictureModalOpen}
          setPictureList={setPictureList}
          pictureList={pictureList}
          addIconPosition={addIconPosition}
          selectedFromEmpty={selectedFromEmpty}
          imageCount={imageCount}
          setImageCount={setImageCount}
          setSnackFlag={setSnackFlag}
          setVideoTrimmerOpen={setVideoTrimmerOpen}
          setSelectedVideoFile={setSelectedVideoFile}
          totalDuration={totalDuration}
        />

        <FreeFormCropModal
          open={showFreeFormModal}
          onClose={() => setShowFreeFormModal(false)}
          selectedImageUrl={pictureList[selectedImage]}
          setPictureList={setPictureList}
        />

        <RatioCropModal
          open={showRatioModal}
          onClose={() => setShowRatioModal(false)}
          selectedImageUrl={pictureList[selectedImage]}
          setPictureList={setPictureList}
        />

        {/* NEW: Video Trimmer Modal */}
        <VideoTrimmerModal
          open={videoTrimmerOpen}
          onClose={() => {
            setVideoTrimmerOpen(false);
            setSelectedVideoFile(null);
          }}
          videoFile={selectedVideoFile}
          onTrimComplete={handleVideoTrimComplete}
          maxDuration={60}
          remainingDuration={60 - totalDuration}
        />

        <PromptModal
          isOpen={isDiscardModalOpen}
          onClose={() => setIsDiscardModalOpen(false)}
          title={TEXT_DIALOG_TITLE}
          desc={TEXT_DIALOG_MESSAGE}
          redButtonText={BUTTON_DIALOG_DISCARD}
          onRedButtonClick={() => {
            setIsDiscardModalOpen(false);
            goBackAndReset();
          }}
          blueButtonText={BUTTON_TEXT_CANCEL}
          onBlueButtonClick={() => setIsDiscardModalOpen(false)}
        />
      </Box>
    </DndProvider>
  );
}
